import React from 'react';
import PropTypes from 'prop-types';

import Avatar from '../user/avatar';
import Name from '../user/name';

import Togglable from "../togglable";

import { formatTimestamp, formatDate } from '../../util';

import { IComment, ObjectID } from '../../types'


interface ITypeTag {
	icon: "lock" | "school" | "announcement" | "public"
	label: string
}

const TypeTag = ({ icon, label }: ITypeTag) => (
	<small>
		<i aria-hidden="true" className="material-icons">{icon}</i>
		&nbsp;
		<span className="badge bg-light-1">{label}</span>
	</small>
)


interface ICommentType {
	comment: IComment
}

const CommentType = ({ comment }: ICommentType) => {
	switch (comment.comment_type) {
		case "personal": case "archived_personal": return <TypeTag icon="lock" label="Private" />;
		case "course": case "archived_course": return <TypeTag icon="school" label="Course" />;
		case "feedback": case "archived_feedback": return <TypeTag icon="announcement" label="Feedback" />;
		case "public": case "archived_public": return <TypeTag icon="public" label="Public comment" />;
		default: return null;
	}
}


interface ITimelineType {
	comment: IComment
}

const TimelineType = ({ comment }: ITimelineType) => {
	if (comment.timestamp === 0) {
		return <small><span className="badge bg-light-1">General</span></small>
	} else {
		return <small><span className="badge bg-light-1">Timeline</span></small>
	}
}


interface ITimestamp {
	timestamp: number
	seek: (timestamp: number) => void
}

const Timestamp = Togglable(({ timestamp, seek }: ITimestamp) => (
	<small className="text-black-secondary">
		<span className="text-hint" style={{cursor:"pointer"}} onClick={() => seek(timestamp)}>{formatTimestamp(timestamp)}</span>
	</small>
))


interface IButton {
	className: string
	onClick: () => void
	children: React.ReactNode
}

const Button = Togglable(({ className="btn btn-flat-secondary btn-sm unsw-mw-0", onClick, children }: IButton) => (
	<button className={className} onClick={onClick} type="button">{children}</button>
))


interface IMetadata {
	comment: IComment
	allow_reply: boolean
	startCommentReply: (objectid: ObjectID) => void
}

const Metadata = Togglable(({ comment, allow_reply, startCommentReply }: IMetadata) => (
	<p className="mb-0 text-black-secondary">
		<CommentType comment={comment} />
		&nbsp;
		<TimelineType comment={comment} />
		&nbsp;
		<Button visible={allow_reply} onClick={()=>startCommentReply(comment.objectid)}>Reply</Button>
	</p>
))


interface IOptionMenuItem {
	children: React.ReactNode
	icon: string
	onClick: () => void
}

const OptionMenuItem = Togglable(({ children, icon, onClick }: IOptionMenuItem) => (
	<button className="dropdown-item" onClick={onClick}>
		<small><i aria-hidden="true" className="material-icons align-text-top mr-3 text-black-secondary">{icon}</i></small>
		{children}
	</button>
))


interface IOptionMenuItem {
	children: React.ReactNode
	open: boolean
	onToggle: () => void
}

const OptionMenu = Togglable(({ children, open, onToggle }: IOptionMenuItem) => (
	<div className={"dropdown"+(open?" show":"")}>
		<button onClick={onToggle} aria-expanded={open} aria-haspopup="true" aria-label="Toggle dropdown menu" className="btn btn-float btn-sm shadow-none" data-toggle="dropdown" type="button">
			<i aria-hidden="true" className="material-icons">more_vert</i>
		</button>
		<div aria-label="Settings dropdown menu" className={"dropdown-menu dropdown-menu-right dropdown-menu-sm unsw-mw-0"+(open?" show":"")}>
			{children}
		</div>
	</div>
))


interface ICommentProps {
	comment: IComment
	menu_opened: boolean
	allow_reply: boolean
	allow_update: boolean
	allow_delete: boolean
	seek: (timestamp: number) => void
	showCommentMenu: (objectid: ObjectID) => void
	startCommentReply: (objectid: ObjectID) => void
	startEditingComment: (objectid: ObjectID) => void
	removeComment: (objectid: ObjectID) => void
	children: React.ReactNode
}

const Comment = ({ comment, menu_opened, allow_reply, allow_update, allow_delete, seek, showCommentMenu, startCommentReply, startEditingComment, removeComment, children }: ICommentProps) => (
	<div className="list-group-item border-0 mt-3 p-0">
		<div className="d-flex" id={comment.objectid}>
			<Avatar user={comment.creator} />

			<div className="flex-fill">
				<div className="d-flex">
					<div className="flex-fill">
						<p className="mb-0 typography-body-2">
							{comment.comment_type.slice(0, 9)==="archived_" ? <span><i>Deleted</i></span> : <Name user={comment.creator} />}
							&nbsp;
							<Timestamp visible={comment.timestamp !== 0} timestamp={comment.timestamp} seek={seek} />
						</p>

						{comment.comment_type.slice(0, 9) !== "archived_" &&
							<p className="mb-0">
								<span>{comment.body}</span>
							</p>
						}

						<Metadata visible={comment.comment_type.slice(0, 9) !== "archived_"} comment={comment} allow_reply={allow_reply} startCommentReply={startCommentReply} />
					</div>
					<div className="flex-shrink-0">
						<OptionMenu visible={allow_update || allow_delete} open={menu_opened} onToggle={()=>showCommentMenu(comment.objectid)}>
							<OptionMenuItem visible={allow_update} icon="edit" onClick={()=>startEditingComment(comment.objectid)}>Edit comment</OptionMenuItem>
							<OptionMenuItem visible={allow_delete} icon="delete" onClick={()=>removeComment(comment.objectid)}>Delete comment</OptionMenuItem>
						</OptionMenu>
					</div>
				</div>
				{children}
			</div>
		</div>
	</div>
)


export default Comment;